import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  isError, isFinishedUpdating, isUpdating, ResourcesItem,
} from 'redux-and-the-rest';
import { Error } from '../../models/error';

import { UserModel } from '../../models/userModel';
import { getUser, updateUser } from '../../redux/reducers/userReducer';
import { ApplicationState } from '../../redux/types';
import { Routes } from '../../routes';
import { buildErrorsDictionary, ErrorMap } from '../../utils/ErrorsUtils';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '../../utils/FormUtils';
import Checkbox from '../shared/CheckboxInput';
import Header from '../shared/Header';
import LoadingIndicator from '../shared/LoadingIndicator';
import TextInput from '../shared/TextInput';

interface UserDetailFields {
  email: string;
  firstName: string;
  lastName: string;
  marketingOptIn: boolean;
  password?: string;
  passwordConfirmation?: string;
  oldPassword?: string;
}

// A union of the keys of the registration model and user model.
type UserDetailErrorField = Extract<keyof UserDetailFields, keyof UserModel>

interface Props {
  onSubmit(formFields: UserDetailFields): void;
  user: ResourcesItem<UserModel>
}

const MyAccount = ({ onSubmit, user }: Props): React.ReactElement => {
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [passwordChanged, setPasswordChanged] = useState(false);
  const history = useHistory();

  const {
    register, handleSubmit, formState: { errors, isSubmitted }, getValues, setError, setValue, control,
  } = useForm<UserDetailFields>({
    defaultValues: {
      email: user.values.email,
      firstName: user.values.firstName,
      lastName: user.values.lastName,
      marketingOptIn: user.values.marketingOptIn,
    },
  });

  const submit = handleSubmit(({
    firstName, lastName, email, marketingOptIn, oldPassword, password, passwordConfirmation,
  }) => {
    setSuccessMessage(undefined);
    setPasswordChanged(!!password);

    onSubmit({
      firstName, lastName, email, marketingOptIn, oldPassword, password, passwordConfirmation,
    });
  });

  useEffect(() => {
    if (isError(user)) {
      const { status: { errors: remoteErrors } } = user;
      const errorMap: ErrorMap<UserDetailErrorField> = buildErrorsDictionary<UserDetailErrorField>(remoteErrors);

      // Map API errors to our fields via the pointer
      errorMap.forEach((error: Error, key: UserDetailErrorField) => setError(key, {
        message: error.detail,
      }));
    } else if (isFinishedUpdating(user)) {
      if (passwordChanged) {
        const querystring = `?redirectTo=${encodeURIComponent(Routes.Session.NewCredentials)}`;
        const redirectTo = Routes.Session.Destroy.concat(querystring);

        history.push(redirectTo);
      }

      /**
       * If we return to My Account, the user is in a FinishedUpdating state.
       * Ensure the success message is only displayed if the form has been submitted
       */
      if (isSubmitted) {
        setSuccessMessage('Your details have been successfully updated');
      }

      /**
       * we could use the reset function here that is built into react hook form, but that
       * would require us setting default values from the user.values on the form, and then
       * updaing the default values via an effect if the user updated.
       * this is just simpler for the 3 password fields that require clearing
       */
      const resetFields: (keyof UserDetailFields)[] = ['oldPassword', 'password', 'passwordConfirmation'];
      resetFields.forEach((field: (keyof UserDetailFields)) => setValue(field, ''));
    }
  }, [user]);

  const isUpdatingUser = isUpdating(user);

  return (
    <div className="my-account loadable">
      {isUpdatingUser ? (
        <LoadingIndicator />
      ) : (
        <form className="form" onSubmit={submit}>
          <div className="form-section">
            <Header variant="h2" title="Update Details" />

            <div className="form-group">
              <TextInput
                disable
                label="Email"
                useFormRegisterReturn={register('email')}
              />
            </div>

            <div className="form-group">
              <TextInput
                label="First Name"
                useFormRegisterReturn={register('firstName', {
                  required: 'First name is required',
                })}
                error={errors.firstName?.message}
              />
            </div>

            <div className="form-group">
              <TextInput
                label="Last Name"
                useFormRegisterReturn={register('lastName', {
                  required: 'Last name is required',
                })}
                error={errors.lastName?.message}
              />
            </div>

            <div className="form-group">
              <Controller
                name="marketingOptIn"
                control={control}
                render={({
                  field: {
                    value, onChange, name,
                  },
                }) => (
                  <Checkbox
                    name={name}
                    check={!!value}
                    onChange={onChange}
                  >
                    Allow us to send you emails and marketing
                  </Checkbox>
                )}
              />
            </div>
          </div>

          <div className="form-section">
            <Header variant="h2" title="Change Password" />

            <div className="form-group">
              <TextInput
                require
                type="password"
                label="Old Password"
                useFormRegisterReturn={register('oldPassword', {
                  validate: (value) => {
                    /**
                     * if a User has entered anything in the new passsord, or confirmation, field
                     * then their old password is required
                     */
                    const hasNewPassword = Boolean(getValues('password'));
                    const hasOldPassword = Boolean(value);
                    const error = hasNewPassword && !hasOldPassword;
                    return !error || 'In order to change your password, you must enter your current password';
                  },
                })}
                error={errors.oldPassword?.message}
              />
            </div>

            <div className="form-group">
              <TextInput
                require
                type="password"
                label="New Password"
                useFormRegisterReturn={register('password', {
                  minLength: {
                    value: PASSWORD_MIN_LENGTH,
                    message: `Your password must be at least ${PASSWORD_MIN_LENGTH} characters long`,
                  },
                  maxLength: {
                    value: PASSWORD_MAX_LENGTH,
                    message: `Your password cannot be longer than ${PASSWORD_MAX_LENGTH} characters`,
                  },
                })}
                error={errors.password?.message}
              />
            </div>

            <div className="form-group">
              <TextInput
                require
                type="password"
                label="Re-enter Password"
                useFormRegisterReturn={register('passwordConfirmation', {
                  validate: (value) => {
                    const newPassword = getValues('password');
                    const hasNewPassword = Boolean(newPassword);

                    // If a new password is entered, this value should match
                    if (hasNewPassword) {
                      if (value !== newPassword) {
                        return 'Your password confirmation does not match';
                      }
                    }

                    // no error
                    return true;
                  },
                })}
                error={errors.passwordConfirmation?.message}
              />
            </div>
          </div>

          {!!successMessage && (
            <div className="form-section form-message success">
              {successMessage}
            </div>
          )}

          <div className="form-section form-actions">
            <button className="button primary" type="submit" disabled={isUpdatingUser}>
              Update
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const mapReduxStateToProps = (({ users }: ApplicationState) => ({
  user: getUser(users),
  onSubmit: ({
    firstName, lastName, marketingOptIn, password, passwordConfirmation, oldPassword,
  }: UserDetailFields) => {
    const user = getUser(users);
    const { id } = user.values;

    updateUser({
      id,
      type: 'user',
      firstName,
      lastName,
      marketingOptIn,
      oldPassword,
      password,
      passwordConfirmation,
      include: 'schools',
    });
  },
}));

export default connect(mapReduxStateToProps)(MyAccount);
